/**
 * Good Atoms Daily "Undring" — Wonder of the Day Widget
 *
 * A home-page card voiced by a rotating Good Atoms mascot.
 *   • Mascot peeks in from the left with its idle animation
 *   • Speech-bubble question pulses gently
 *   • Hint text fades in below
 *   • "Utforsk mer →" navigates to the linked lesson (if any)
 *   • Rotates daily via dayOfYear % pool size
 */

import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Mascot, MASCOT_NAMES } from '../mascots'
import { getTodaysWonder } from '../../data/dailyWonders'
import { useT } from '../../i18n'

// ─── Subject accent colours (matches ConstellationMap STAR_COLORS) ─────────────

const SUBJECT_ACCENT: Record<string, { bg: string; border: string; text: string; btn: string }> = {
  mathematics:  { bg: '#FFFBEB', border: '#FDE68A', text: '#D97706', btn: '#F59E0B' },
  science:      { bg: '#F0FDF4', border: '#BBF7D0', text: '#15803D', btn: '#22C55E' },
  technology:   { bg: '#EFF6FF', border: '#BFDBFE', text: '#1D4ED8', btn: '#60A5FA' },
  engineering:  { bg: '#FEF2F2', border: '#FECACA', text: '#DC2626', btn: '#F87171' },
  arts:         { bg: '#FAF5FF', border: '#E9D5FF', text: '#7C3AED', btn: '#C084FC' },
  nature:       { bg: '#F0FDF4', border: '#BBF7D0', text: '#15803D', btn: '#4ADE80' },
  futureskills: { bg: '#F5F3FF', border: '#DDD6FE', text: '#6D28D9', btn: '#A78BFA' },
}

// ─── Sparkle field — 6 small static sparkles around the card ─────────────────

const SPARKLES: [number, number, number][] = [
  [8, 12, 0], [88, 8, 1], [94, 55, 2], [82, 88, 0], [12, 80, 3], [50, 4, 1],
]

// ─── Component ────────────────────────────────────────────────────────────────

export function DailyWonder() {
  const navigate  = useNavigate()
  const { lang }  = useT()
  const wonder    = getTodaysWonder()
  const accent    = SUBJECT_ACCENT[wonder.mascot] ?? SUBJECT_ACCENT.mathematics
  const mascotName = MASCOT_NAMES[wonder.mascot]
  const question  = lang === 'en' ? wonder.questionEn : wonder.questionNo
  const hint      = lang === 'en' ? wonder.hintEn     : wonder.hintNo

  return (
    <motion.div
      className="relative rounded-3xl overflow-hidden border"
      style={{
        background:   accent.bg,
        borderColor:  accent.border,
        borderWidth:  1.5,
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      {/* ── Floating sparkles ─────────────────────────────────────────────── */}
      {SPARKLES.map(([x, y, phase], i) => (
        <motion.div
          key={i}
          aria-hidden="true"
          className="absolute pointer-events-none select-none"
          style={{
            left:     `${x}%`,
            top:      `${y}%`,
            fontSize: 10 + (i % 3) * 3,
            color:    accent.btn,
            opacity:  0.5,
          }}
          animate={{ opacity: [0.25, 0.7, 0.25], scale: [0.8, 1.15, 0.8] }}
          transition={{
            duration:    2.2 + phase * 0.6,
            repeat:      Infinity,
            ease:        'easeInOut',
            delay:       i * 0.35,
          }}
        >
          ✦
        </motion.div>
      ))}

      <div className="flex items-start gap-4 p-5">

        {/* ── Mascot ──────────────────────────────────────────────────────── */}
        <motion.div
          className="flex-shrink-0 pt-1"
          initial={{ x: -30, opacity: 0 }}
          animate={{ x: 0,   opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1, type: 'spring', stiffness: 200, damping: 20 }}
        >
          <Mascot subject={wonder.mascot} emotion="happy" size={72} />
          <p
            className="text-center text-[10px] font-bold mt-1 select-none"
            style={{ color: accent.text, opacity: 0.8 }}
          >
            {mascotName}
          </p>
        </motion.div>

        {/* ── Content ─────────────────────────────────────────────────────── */}
        <div className="flex-1 min-w-0 pt-1">

          {/* Label */}
          <motion.p
            className="text-[10px] font-extrabold uppercase tracking-[0.15em] mb-1.5 select-none"
            style={{ color: accent.text }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            transition={{ delay: 0.2 }}
          >
            {lang === 'en' ? "Today's Wonder" : 'Dagens undring'}
          </motion.p>

          {/* Question — speech-bubble style with gentle pulse */}
          <motion.p
            className="font-extrabold text-ink leading-snug text-[1.0rem]"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            {question}
          </motion.p>

          {/* Hint */}
          <motion.p
            className="mt-2 text-ink/55 text-sm leading-relaxed"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.4 }}
          >
            {hint}
          </motion.p>

          {/* CTA */}
          {wonder.lessonId && (
            <motion.button
              className="mt-3 text-sm font-extrabold px-4 py-1.5 rounded-full"
              style={{ background: accent.btn, color: 'white' }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7, type: 'spring', stiffness: 260, damping: 20 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(`/lesson/${wonder.lessonId}`)}
            >
              {lang === 'en' ? 'Explore more →' : 'Utforsk mer →'}
            </motion.button>
          )}

        </div>
      </div>
    </motion.div>
  )
}
